import React, { useCallback, useEffect, useState } from "react";
import { Music2, Search as SearchIcon, Play, Pause, SkipForward, SkipBack, LogIn, LogOut, AlertTriangle, Radio as RadioIcon } from "lucide-react";
import useSpotifyAuthHook from "../../lib/useSpotifyAuth";
import useSpotifyPlayerHook from "../../lib/useSpotifyPlayer";
import BridgeToObsModal from "./BridgeToObsModal";

/**
 * SpotifyPanel — collapsible drawer that lets the host sign in with
 * Spotify (PKCE), search the catalog, and play FULL tracks via the Spotify
 * Web Playback SDK.
 *
 * ⚠️ Important: Spotify audio is encrypted via EME/DRM, so it CANNOT flow
 * through the Tap IN DSP engine (EQ/FX/crossfader/recorder/broadcast). The
 * Spotify player is a self-contained stream alongside the decks — the DJ
 * mixes manually by lowering music faders when talking over a Spotify
 * track. This is a platform constraint, not a bug we can fix.
 *
 * Also: Spotify Web Playback requires a Spotify Premium subscription.
 *
 * Props:
 *   - auth        (optional)  external useSpotifyAuth() instance — when
 *                              the parent (RadioHostStudio) lifts auth so
 *                              decks can also drive Spotify playback.
 *   - player      (optional)  external useSpotifyPlayer() instance for the
 *                              same reason.
 *   - onSendToDeck(side, t)   when set, renders → A / → B buttons on each
 *                              result row so the host can pin a Spotify
 *                              track to a deck for monitoring.
 */
export default function SpotifyPanel({ auth: extAuth, player: extPlayer, onSendToDeck }) {
  const internalAuth = useSpotifyAuthHook();
  const auth = extAuth || internalAuth;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [nowPlayingUri, setNowPlayingUri] = useState(null);
  const [bridgeOpen, setBridgeOpen] = useState(false);

  // Only spin up an internal player when the parent didn't supply one,
  // otherwise we'd boot two SDK instances and Spotify will reject the
  // duplicate device.
  const internalPlayer = useSpotifyPlayerHook({
    enabled: !extPlayer && open && auth.loggedIn,
    getFreshAccessToken: auth.getFreshAccessToken,
  });
  const player = extPlayer || internalPlayer;

  // Handle the PKCE callback: code arrives at /spotify/callback?code=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const hasReturn = localStorage.getItem("tapin_spotify_return");
    if (code && hasReturn) {
      auth.exchangeCode(code).finally(() => {
        const backTo = localStorage.getItem("tapin_spotify_return") || "/";
        localStorage.removeItem("tapin_spotify_return");
        // Replace so the ?code= doesn't get re-processed
        window.history.replaceState({}, "", backTo);
      });
    }
    // eslint-disable-next-line
  }, []);

  const search = useCallback(async () => {
    const tok = await auth.getFreshAccessToken();
    if (!tok || !q.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`https://api.spotify.com/v1/search?type=track&limit=20&q=${encodeURIComponent(q.trim())}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) { setResults([]); return; }
      const data = await res.json();
      setResults(data?.tracks?.items || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, [auth, q]);

  const play = async (trk) => {
    const ok = await player.play(trk.uri);
    if (ok) setNowPlayingUri(trk.uri);
  };

  const s = player.state;
  const currentTrack = s?.track_window?.current_track;
  const paused = !!s?.paused;

  if (!auth.configured) {
    return (
      <div data-testid="radio-host-spotify" className="rounded-lg border border-white/10 bg-black/30 p-3">
        <div className="flex items-center gap-2 text-white/70">
          <Music2 className="w-3.5 h-3.5 text-emerald-400" />
          <div className="font-black text-[11px] uppercase tracking-wider">Spotify</div>
        </div>
        <div className="mt-2 text-[10px] text-white/50 leading-snug">
          Not configured yet. An admin needs to add the Spotify Client ID in <strong>Admin → Integrations</strong>. Once set, everyone on Tap IN can sign in with Spotify Premium to play full tracks here.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="radio-host-spotify" className="rounded-lg border border-white/10 bg-black/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <Music2 className="w-3.5 h-3.5 text-emerald-400" />
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-[11px] uppercase tracking-wider">Spotify · Full tracks</div>
          <div className="text-white/50 text-[10px] truncate">
            {auth.loggedIn
              ? (player.ready ? (currentTrack ? `${currentTrack.name} · ${currentTrack.artists?.[0]?.name}` : "Ready · search a song") : "Connecting player…")
              : "Sign in with Spotify Premium to play full songs"}
          </div>
        </div>
        {auth.loggedIn && <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-[9px] font-black px-2 py-0.5 rounded-full">ON</span>}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {/* DRM limitation notice + Bridge-to-OBS escape hatch. */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 space-y-1.5" data-testid="spotify-drm-warning">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 text-amber-300 shrink-0 mt-[1px]" />
              <div className="text-amber-100/90 text-[9px] leading-snug">
                <strong>Monitor only.</strong> Spotify audio is DRM-protected — it plays through your speakers / headphones for monitoring while you mix, but it can't route through the Tap IN EQ/FX/recorder. <strong>Listeners on the in-app player will hear silence on Spotify decks.</strong>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBridgeOpen(true)}
              data-testid="spotify-bridge-to-obs"
              className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-500/40 font-bold text-[10px] uppercase tracking-wider rounded py-1.5"
            >
              <RadioIcon className="w-3 h-3" /> Bridge to OBS — broadcast Spotify
            </button>
          </div>
          <BridgeToObsModal open={bridgeOpen} onClose={() => setBridgeOpen(false)} />

          {!auth.loggedIn ? (
            <button
              data-testid="spotify-login"
              onClick={() => auth.login().catch((e) => alert(e.message))}
              className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[11px] py-1.5 rounded"
            >
              <LogIn className="w-3 h-3" /> Sign in with Spotify
            </button>
          ) : (
            <>
              {/* Now playing + transport */}
              {currentTrack && (
                <div data-testid="spotify-now-playing" className="bg-white/5 rounded p-2 flex items-center gap-2">
                  {currentTrack.album?.images?.[0]?.url && (
                    <img src={currentTrack.album.images[0].url} alt="" className="w-10 h-10 rounded shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[11px] font-bold truncate">{currentTrack.name}</div>
                    <div className="text-white/60 text-[9px] truncate">{(currentTrack.artists || []).map((a) => a.name).join(", ")}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid="spotify-prev" onClick={player.prev} className="p-1.5 bg-white/10 hover:bg-white/20 rounded"><SkipBack className="w-3 h-3" /></button>
                    {paused ? (
                      <button data-testid="spotify-resume" onClick={player.resume} className="p-1.5 bg-white text-black rounded"><Play className="w-3 h-3" /></button>
                    ) : (
                      <button data-testid="spotify-pause" onClick={player.pause} className="p-1.5 bg-white text-black rounded"><Pause className="w-3 h-3" /></button>
                    )}
                    <button data-testid="spotify-next" onClick={player.next} className="p-1.5 bg-white/10 hover:bg-white/20 rounded"><SkipForward className="w-3 h-3" /></button>
                  </div>
                </div>
              )}

              {/* Volume */}
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest text-white/50 font-black">VOL</span>
                <input
                  data-testid="spotify-volume"
                  type="range" min="0" max="1" step="0.02"
                  value={player.volume}
                  onChange={(e) => player.setVolume(parseFloat(e.target.value))}
                  className="flex-1 accent-emerald-400 h-1"
                />
                <span className="text-[9px] font-mono text-white/60 tabular-nums">{Math.round(player.volume * 100)}</span>
              </div>

              {/* Search */}
              <div className="flex items-center gap-1">
                <SearchIcon className="w-3 h-3 text-white/50" />
                <input
                  data-testid="spotify-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                  placeholder="Search Spotify…"
                  className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-[11px]"
                />
                <button
                  data-testid="spotify-search-btn"
                  onClick={search}
                  disabled={searching || !q.trim()}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[10px] px-2 py-1 rounded disabled:opacity-40"
                >{searching ? "…" : "Go"}</button>
              </div>

              {/* Results */}
              <div className="space-y-1 max-h-[260px] overflow-y-auto" data-testid="spotify-results">
                {results.length === 0 && !searching && (
                  <div className="text-white/40 text-[10px] text-center py-2">
                    Type a song / artist and hit Go.
                  </div>
                )}
                {results.map((t) => (
                  <div key={t.id} data-testid={`spotify-row-${t.id}`} className={`flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded p-1.5 ${nowPlayingUri === t.uri ? "ring-1 ring-emerald-400" : ""}`}>
                    {t.album?.images?.[2]?.url && <img src={t.album.images[2].url} alt="" className="w-8 h-8 rounded shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-[10px] font-bold truncate">{t.name}</div>
                      <div className="text-white/60 text-[9px] truncate">{(t.artists || []).map((a) => a.name).join(", ")}</div>
                    </div>
                    <button
                      data-testid={`spotify-play-${t.id}`}
                      onClick={() => play(t)}
                      disabled={!player.ready}
                      className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-30 text-black font-black text-[9px] px-2 py-1 rounded inline-flex items-center gap-1"
                    ><Play className="w-2.5 h-2.5" /> Play</button>
                    {onSendToDeck && (
                      <>
                        <button
                          data-testid={`spotify-to-deck-a-${t.id}`}
                          onClick={() => onSendToDeck("A", t)}
                          title="Pin this Spotify track on Deck A (monitor only — listeners hear silence on this deck unless you broadcast via OBS)"
                          className="bg-sky-500/30 hover:bg-sky-500 text-sky-100 hover:text-black font-black text-[9px] px-1.5 py-1 rounded"
                        >→A</button>
                        <button
                          data-testid={`spotify-to-deck-b-${t.id}`}
                          onClick={() => onSendToDeck("B", t)}
                          title="Pin this Spotify track on Deck B (monitor only — listeners hear silence on this deck unless you broadcast via OBS)"
                          className="bg-rose-500/30 hover:bg-rose-500 text-rose-100 hover:text-black font-black text-[9px] px-1.5 py-1 rounded"
                        >→B</button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Player / auth status */}
              {player.error && (
                <div className="text-red-300 text-[9px] bg-red-500/10 border border-red-500/30 rounded px-2 py-1" data-testid="spotify-error">
                  {player.error}
                </div>
              )}
              <button
                data-testid="spotify-logout"
                onClick={() => auth.logout()}
                className="w-full inline-flex items-center justify-center gap-1 bg-white/5 hover:bg-red-500/30 border border-white/10 text-white/70 hover:text-red-200 font-black text-[10px] py-1 rounded"
              >
                <LogOut className="w-2.5 h-2.5" /> Sign out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
